/* eslint-disable max-lines-per-function */
import { useMutation, useQuery } from "@apollo/client";
import { Grid, CircularProgress, Select, MenuItem, FormGroup, FormControlLabel, Switch } from "@material-ui/core";
import { RouteComponentProps } from "@reach/router";
import Papa from "papaparse";
import React, { useState } from "react";
import { SelectAutocomplete } from "components/haven-design/form/select";
import { Button, Card } from "components/haven-design";
import {
  ImportListCSVMutation,
  ImportListCSVMutationVariables,
  InterestListsWithOrgQuery,
  InterestListsWithOrgQuery_interestLists as InterestListType,
} from "types/gql-types";
import { ImportListMutation, InterestListsQuery } from "../queries";

import { fields } from "../fields";
import { Payload, Record, Timezone } from "../types";
import {
  Problem,
  verifyDateOrder,
} from "../validate";
import ErrorBoundary from "./boundary";
import Fields from "./fields";

import Summary from "./summary";
import Upload from "./upload";
import Validations from "./validations";
import { csvPreprocessor, csvPostprocessor, deriveGraphqlProblems, deriveValidationProblems, onUploadCSV, validators } from "./utils";

// eslint-disable-next-line max-lines-per-function
const SpreadsheetImporter: React.FC<RouteComponentProps> = () => {
  const [payload, setPayload] = React.useState<Payload<Record>>({
    data: [],
    file: null,
    mapping: {},
    timezone: "none",
  });
  const [orderValidation, setOrderValidation] = React.useState(true);

  // problems is a set of all validation errors or warnings with the data or
  // mappings.
  const [problems, setProblems] = React.useState<Problem[]>([]);

  const toggleOrderValidation = () => setOrderValidation((old) => !old);
  React.useEffect(() => {
    const currentValidators = validators.concat(orderValidation ? [verifyDateOrder] : []);
    setProblems(deriveValidationProblems(currentValidators, payload));
  }, [orderValidation, payload]);

  const { loading, data, error } = useQuery<InterestListsWithOrgQuery>(InterestListsQuery);
  const [interestList, setInterestList] = useState<InterestListType | null>(null);

  const [importList, importListResults] = useMutation<
  ImportListCSVMutation,
  ImportListCSVMutationVariables
  >(ImportListMutation);

  const cleanAndImport = async () => {
    if (payload.file === null) {
      throw new Error("unexpectedly missing file object");
    }

    const processed = csvPostprocessor(payload);
    const serialized = Papa.unparse(processed.data);

    const variables = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: interestList!.id,
      csv: serialized,
    };

    await importList({ variables });
  };

  const handleUpload = onUploadCSV((result, file) => {
    const preprocessed = csvPreprocessor({ ...payload, file, data: result.data });

    // When the header names match our own field names exactly, populate
    // the mapping field ahead of time.
    const [candidate] = preprocessed.data;
    if (!candidate) {
      throw new Error("insufficient data to derive headers from.");
    }

    const mapping = Object.entries(fields).reduce(
      (memo, [key]) => {
        const heading = Object.keys(candidate).find(record => record.trim() === key);

        return (heading ? { ...memo, [heading]: { field: key, format: "" } } : memo);
      },
      {},
    );

    setPayload({ ...preprocessed, mapping });
  });

  if (loading) {
    return (
      <Grid container item justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  if (error || !data?.interestLists) return <p>There was an error fetching the interest lists</p>;

  return (
    <ErrorBoundary>
      <Card>
        <h1>Clean & Import Waitlist Spreadsheet</h1>

        <h4>Timezone</h4>
        <p>Select the timezone which the property resides in.</p>
        <Select
          variant="outlined"
          margin="dense"
          value={payload.timezone || "none"}
          onChange={event => setPayload({ ...payload, timezone: event.target.value as Timezone })}
        >
          <MenuItem value="none" disabled>Choose</MenuItem>
          <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
          <MenuItem value="America/Denver">Mountain Time</MenuItem>
          <MenuItem value="America/Chicago">Central Time</MenuItem>
          <MenuItem value="America/New_York">Eastern Time</MenuItem>
        </Select>
      </Card>

      {payload.timezone !== "none" as Timezone && (
        <Card>
          <h4>Select a CSV from your computer</h4>
          <p>
            CSVs larger than 500 rows might fail or timeout.
            Only have an Excel spreadsheet?&nbsp;
            <a
              href="https://support.office.com/en-us/article/Import-or-export-text-txt-or-csv-files-5250ac4c-663c-47ce-937b-339e391393ba"
              target="_blank" rel="noreferrer"
            >
              Export it as a CSV
            </a>
            .
          </p>
          <Upload onSelect={handleUpload} />
        </Card>
      )}

      {payload.file !== null && (
        <>
          <Card>
            <h4>Summary</h4>
            <Summary state={payload} />
          </Card>

          <Card>
            <h4>Validation</h4>
            <FormGroup>
              <FormControlLabel
                control={<Switch checked={orderValidation} onChange={toggleOrderValidation} />}
                label={orderValidation ? "Date/time in order" : "Date/time order is not required"} />
            </FormGroup>
            <Validations problems={problems} />
          </Card>

          <Card>
            <ErrorBoundary message="An unexpected error was encountered while displaying the contents of the CSV.">
              <h4>Column name mapping</h4>
              <Fields
                state={payload}
                onSetMapping={(from, to) =>
                  setPayload({
                    ...payload,
                    mapping: { ...payload.mapping, [from]: { field: to, format: "" } },
                  })
                }
                onSetFormat={(from, format) => {
                  setPayload({
                    ...payload,
                    mapping: {
                      ...payload.mapping,
                      [from]: { field: payload.mapping[from].field, format },
                    },
                  });
                }}
              />
            </ErrorBoundary>
          </Card>

          <Card>
            <SelectAutocomplete
              label="Interest List"
              value={interestList}
              getOptionLabel={(option) => `${option?.name} (${option?.organization?.name})`}
              options={data.interestLists}
              onChange={(_, value) => setInterestList(value)}
            />
            <Button onClick={cleanAndImport} color="primary" disabled={importListResults.loading || !interestList}>
              Import
            </Button>
          </Card>
        </>
      )}

      {importListResults.called && importListResults.loading && (
        <Card>
          <Grid container item justifyContent="center">
            <CircularProgress /> Importing ...
          </Grid>
        </Card>
      )}

      {importListResults.called && !importListResults.loading && (
        <Card>
          <h4>Import Results</h4>

          {importListResults.data?.importInterestList?.success && (
            <p>Import Successful!</p>
          )}

          {importListResults.error && (
            <>
              <p>
                Something went wrong, please check the
                above validation issues as well as review
                the table below.
              </p>

              <Validations
                problems={deriveGraphqlProblems(importListResults.error.graphQLErrors, payload)} />
            </>
          )}
        </Card>
      )}
      <div />
    </ErrorBoundary>
  );
};

export default SpreadsheetImporter;
